// Versions du produit (lot G4 v0.5.0 — B-254, ART-0170) : client de `/api/v1/releases`.
// Préparation (contenu, « prête ? » et ses raisons), approbation par environnement, notes de
// version, déploiement par le chemin RunOps, documentation de livraison.
import { ApiError, BASE, httpMessage, request } from './http'
import { getToken } from '../auth/session'
import type {
  ReleaseApproval, ReleaseCandidates, ReleaseContentInput, ReleaseCreateInput, ReleaseDeployCheck, ReleaseDetail,
  ReleaseDocumentKind, ReleaseDocumentSummary, ReleaseListResponse, ReleaseNoteVersion, ReleaseNotesHistory,
  ReleaseReadiness, ReleaseStatus, ReleaseVersion,
} from './types/releases'
import type { VersionReadinessReponse } from '@/lib/livrer'

function query(params: Record<string, string | number | null | undefined>): string {
  const q = new URLSearchParams()
  for (const [cle, valeur] of Object.entries(params)) {
    if (valeur !== undefined && valeur !== null && valeur !== '') q.set(cle, String(valeur))
  }
  return q.toString()
}

const r = (releaseId: string): string => `/releases/${encodeURIComponent(releaseId)}`

export const releasesApi = {
  /** Versions de l'espace (dans le cycle ou héritées de RunOps), la plus récente d'abord. */
  listReleaseVersions: (workspaceId: string) =>
    request<ReleaseListResponse>(`/releases?${query({ workspace_id: workspaceId })}`),
  createReleaseVersion: (input: ReleaseCreateInput) =>
    request<ReleaseVersion>('/releases', { method: 'POST', body: JSON.stringify(input) }),
  /** Fiche : contenu, « prête ? », environnements, actions expliquées avant le clic. */
  getReleaseVersion: (workspaceId: string, releaseId: string, environmentId?: string) =>
    request<ReleaseDetail>(`${r(releaseId)}?${query({ workspace_id: workspaceId, environment_id: environmentId })}`),
  getReleaseReadiness: (workspaceId: string, releaseId: string, environmentId?: string) =>
    request<ReleaseReadiness>(`${r(releaseId)}/readiness?${query({ workspace_id: workspaceId, environment_id: environmentId })}`),
  /**
   * « Prête ? » à 12 raisons (ARB-r2-15) : `GET /versions/{id}/readiness` → `{ reasons: [{ code, etat, objet }] }`.
   * Présent sur la branche d'intégration, pas encore partout : `null` si la passerelle ne l'expose
   * pas (404, 405, 501) — l'écran retombe alors sur le calcul de la fiche et le dit.
   */
  getVersionReadiness: async (workspaceId: string, releaseId: string): Promise<VersionReadinessReponse | null> => {
    try {
      return await request<VersionReadinessReponse>(
        `/versions/${encodeURIComponent(releaseId)}/readiness?${query({ workspace_id: workspaceId })}`)
    } catch (e) {
      if (e instanceof ApiError && [404, 405, 501].includes(e.status)) return null
      throw e
    }
  },
  listReleaseCandidates: (workspaceId: string) =>
    request<ReleaseCandidates>(`/releases/candidates?${query({ workspace_id: workspaceId })}`),
  adoptRelease: (workspaceId: string, releaseId: string) =>
    request<ReleaseVersion>(`${r(releaseId)}/adopt`, { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId }) }),
  addReleaseContent: (releaseId: string, input: ReleaseContentInput) =>
    request<{ id: number; created: boolean }>(`${r(releaseId)}/contents`, { method: 'POST', body: JSON.stringify(input) }),
  removeReleaseContent: (workspaceId: string, releaseId: string, contentId: number) =>
    request<void>(`${r(releaseId)}/contents/${contentId}?${query({ workspace_id: workspaceId })}`, { method: 'DELETE' }),
  linkReleaseCampaign: (workspaceId: string, releaseId: string, campaignId: number) =>
    request<{ linked: boolean }>(`${r(releaseId)}/campaigns/${campaignId}`,
      { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId }) }),
  /**
   * « Construire et signer la révision » (RJ-46) : le worker hôte construit l'image de la révision
   * retenue, produit son SBOM, signe la provenance et l'écrit au registre (raison 12). 202 : job enfilé ;
   * 200 : déjà enregistrée (`recorded`) ou déjà en construction (`queued`, job existant).
   */
  /** `renew` : reconstruire et re-signer une révision déjà signée (image signée disparue de l'hôte, RJ-82). */
  buildSignRevision: (workspaceId: string, releaseId: string, revision: string, renew = false) =>
    request<{ release_id: string; revision: string; status: 'queued' | 'recorded'; job_id?: string; created: boolean }>(
      `${r(releaseId)}/revisions/${encodeURIComponent(revision)}/build-sign`,
      { method: 'POST', body: JSON.stringify(renew ? { workspace_id: workspaceId, renew: true } : { workspace_id: workspaceId }) }),
  transitionRelease: (releaseId: string, input: { workspace_id: string; to_status: ReleaseStatus; reason?: string }) =>
    request<ReleaseVersion>(`${r(releaseId)}/transitions`, { method: 'POST', body: JSON.stringify(input) }),
  requestReleaseApproval: (releaseId: string, input: { workspace_id: string; environment_id: string; note?: string }) =>
    request<ReleaseApproval>(`${r(releaseId)}/approvals`, { method: 'POST', body: JSON.stringify(input) }),
  reviewReleaseApproval: (releaseId: string, approvalId: number, input: { workspace_id: string; note?: string }) =>
    request<ReleaseApproval>(`${r(releaseId)}/approvals/${approvalId}/review`, { method: 'POST', body: JSON.stringify(input) }),
  decideReleaseApproval: (releaseId: string, approvalId: number,
                          input: { workspace_id: string; decision: 'approve' | 'reject'; note?: string }) =>
    request<ReleaseApproval>(`${r(releaseId)}/approvals/${approvalId}/decision`, { method: 'POST', body: JSON.stringify(input) }),
  withdrawReleaseApproval: (releaseId: string, approvalId: number, input: { workspace_id: string; reason: string }) =>
    request<ReleaseApproval>(`${r(releaseId)}/approvals/${approvalId}/withdraw`, { method: 'POST', body: JSON.stringify(input) }),
  /** Peut-on déployer ? Toutes les raisons, sans effet. */
  checkReleaseDeploy: (workspaceId: string, releaseId: string, environmentId: string) =>
    request<ReleaseDeployCheck>(`${r(releaseId)}/deploy-check?${query({ workspace_id: workspaceId, environment_id: environmentId })}`),
  /** Déploie par le chemin RunOps existant (job `deploy_docker`). */
  deployRelease: (releaseId: string, input: { workspace_id: string; environment_id: string; idempotency_key?: string }) =>
    request<{ id: string; status: string; release_id: string; environment_id: string }>(`${r(releaseId)}/deployments`,
      { method: 'POST', body: JSON.stringify(input) }),
  listReleaseNotes: (workspaceId: string, releaseId: string) =>
    request<ReleaseNotesHistory>(`${r(releaseId)}/notes?${query({ workspace_id: workspaceId })}`),
  generateReleaseNotes: (workspaceId: string, releaseId: string) =>
    request<ReleaseNoteVersion & { created: boolean }>(`${r(releaseId)}/notes/generate`,
      { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId }) }),
  editReleaseNotes: (releaseId: string, input: { workspace_id: string; body_md: string; based_on_version: number }) =>
    request<ReleaseNoteVersion>(`${r(releaseId)}/notes`, { method: 'POST', body: JSON.stringify(input) }),
  publishReleaseNotes: (workspaceId: string, releaseId: string, versionNo: number) =>
    request<ReleaseNoteVersion>(`${r(releaseId)}/notes/${versionNo}/publish`,
      { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId }) }),
  listReleaseDocuments: (workspaceId: string, releaseId: string) =>
    request<{ items: ReleaseDocumentSummary[] }>(`${r(releaseId)}/documents?${query({ workspace_id: workspaceId })}`),
  generateReleaseDocuments: (workspaceId: string, releaseId: string) =>
    request<{ items: ReleaseDocumentSummary[] }>(`${r(releaseId)}/documents/generate`,
      { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId }) }),
  /** Téléchargement authentifié (Markdown ou HTML autonome) : le fichier est enregistré par le navigateur. */
  downloadReleaseDocument: async (workspaceId: string, releaseId: string, kind: ReleaseDocumentKind,
                                  format: 'md' | 'html'): Promise<void> => {
    const token = getToken()
    const response = await fetch(`${BASE}${r(releaseId)}/documents/${kind}?${query({ workspace_id: workspaceId, format })}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} })
    if (!response.ok) throw new ApiError(response.status, httpMessage(response.status), await response.text().catch(() => undefined))
    const disposition = response.headers.get('Content-Disposition') ?? ''
    const nom = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `${kind}.${format}`
    const url = URL.createObjectURL(await response.blob())
    const lien = document.createElement('a')
    lien.href = url
    lien.download = nom
    document.body.appendChild(lien)
    lien.click()
    lien.remove()
    URL.revokeObjectURL(url)
  },
}
